import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

export default function AboutSection() {
  const features = [
    { icon: 'ri-draft-line', title: '무료 설계 상담', desc: '전문 디자이너의 1:1 맞춤 설계 상담을 무료로 제공합니다.' },
    { icon: 'ri-shield-check-line', title: '책임 시공 보증', desc: '계약부터 완공까지 사업자 대표가 직접 책임지고 관리합니다.' },
    { icon: 'ri-tools-line', title: '2년 A/S 보증', desc: '시공 완료 후 2년간 무상 A/S를 제공합니다.' },
    { icon: 'ri-money-dollar-circle-line', title: '투명한 견적', desc: '숨겨진 비용 없이 처음부터 끝까지 투명하게 공개합니다.' },
    { icon: 'ri-award-line', title: 'feel green', desc: '강남에서 쌓아온 시공 노하우와 신뢰로 최고의 공간을 만들어 드립니다.' },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible, getItemStyle } = useStaggerAnimation(features.length);
  const { ref: imageRef, isVisible: imageVisible } = useScrollAnimation();
  const { ref: textRef, isVisible: textVisible } = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });

  return (
    <section className="bg-[#f4f2f0] py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-14 md:mb-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">About Us</span>
          <h2 className="text-3xl md:text-5xl font-light text-[#2a2218] mt-3 md:mt-4 leading-snug md:leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            feel green이 만드는<br />
            <span className="font-semibold">완벽한 공간 설계</span>
          </h2>
          <p className="text-[#6b5e4e] text-sm md:text-base mt-4 md:mt-6 max-w-xl mx-auto leading-relaxed">
            feel green은 수천 개의 공간을 새롭게 탄생시키며
            고객의 신뢰를 쌓아왔습니다. 단순한 시공을 넘어 삶의 질을 높이는
            공간을 만들어 드립니다.
          </p>
        </div>

        {/* Feature Cards */}
        <div
          ref={cardsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5"
        >
          {features.map((f, i) => (
            <div
              key={f.title}
              style={getItemStyle(i)}
              className="bg-white rounded-2xl p-7 border border-[#e8e0d0] hover:border-[#c9a84c]/40 transition-colors duration-300 group"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#c9a84c]/10 mb-5 group-hover:bg-[#c9a84c]/20 transition-colors">
                <i className={`${f.icon} text-[#c9a84c] text-xl`} />
              </div>
              <h3 className="text-[#2a2218] font-semibold text-sm mb-2">{f.title}</h3>
              <p className="text-[#8a7a6a] text-xs leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Image + Text Row */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div
            ref={imageRef as React.RefObject<HTMLDivElement>}
            className="relative"
            style={{
              opacity: imageVisible ? 1 : 0,
              transform: imageVisible ? 'translateX(0)' : 'translateX(-48px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <div className="w-full h-[420px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/8a752ad388b237d1c7a7e13c70620751.jpg"
                alt="전문 인테리어 팀"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div
            ref={textRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateX(0)' : 'translateX(48px)',
              transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
            }}
          >
            <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Our Story</span>
            <h3 className="text-2xl md:text-4xl font-light text-[#2a2218] mt-3 md:mt-4 mb-4 md:mb-6 leading-snug md:leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              feel green이<br />
              <span className="font-semibold">책임지는 공간</span>
            </h3>
            <p className="text-[#6b5e4e] text-sm leading-relaxed mb-6 break-keep">
              서울 강남구 양재대로에 자리한 feel green은
              고객 한 분 한 분의 신뢰를 바탕으로 성장해왔습니다.
              아파트, 빌라, 오피스텔부터 상업공간까지 다양한 시공 경험을 보유하고 있으며,
              모든 공사는 사업자 대표가 직접 현장을 관리합니다.
            </p>
            <p className="text-[#6b5e4e] text-sm leading-relaxed mb-8 break-keep">
              투명한 견적과 책임 시공, 그리고 2년 A/S 보증으로
              강남 최고의 인테리어 업체로 자리매김하고 있습니다.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {['30년+ 시공 노하우', '책임 시공 보증', '투명한 견적 공개', '2년 무상 A/S', '친환경 자재 사용', '무료 설계 상담'].map((badge) => (
                <span key={badge} className="border border-[#c9a84c] text-[#c9a84c] text-xs py-2 rounded-full font-semibold bg-[#c9a84c]/10 text-center break-keep">
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
